import { CommonRoles, MemberPermission } from 'constant/Constants';
import ComponentName from '../../constant/ComponentName';
import { CustomFilter } from '../ui/FilterDomains';
import { ActiveRiskType } from '../stream/transaction/TransactionDomains';

export type PermissionType = 'No Permission' | 'View' | 'Edit';
export type UserType = 0 | 1;
export type TenantModuleType = 'fraud' | 'aml' | 'kyc';

export interface BaseDocument {
  id?: string;
}

export interface AuditLog<T = any> extends BaseDocument {
  operation: AuditLogType;
  timestamp: number;
  document: { id: string; data: string };
  username: string;
  ip: string;
  component: ComponentName;
  protocol?: string;
  port?: number;
  reason?: string;
}

export interface ErrorAuditLog<T = any> extends AuditLog<T> {
  reason: string;
}

export interface DependencyAuditLog extends ErrorAuditLog {
  dependencies: Record<string, string[]>;
}

export enum AuditLogType {
  IMPORT = 'IMPORT',
  EXPORT = 'EXPORT',
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  UNDEPLOY = 'UNDEPLOY',
  DEPLOY = 'DEPLOY',
  REDEPLOY = 'REDEPLOY',
  VARIABLE_DELETE = 'VARIABLE_DELETE',
  LOGIN = 'LOGIN',
  COMPONENT_USER_WORK = 'COMPONENT_USER_WORK',
  PAGE_VIEW = 'PAGE_VIEW',
  IMPORT_LIST_DATA = 'IMPORT_LIST_DATA',
  ADD_LIST_DATA = 'ADD_LIST_DATA',
  REMOVE_LIST_DATA = 'REMOVE_LIST_DATA',
  UPDATE_LIST_DATA = 'UPDATE_LIST_DATA',
  ADD_ITEM_TO_CATEGORY = 'ADD_ITEM_TO_CATEGORY',
  ADD_CATEGORY_TO_CATEGORY = 'ADD_CATEGORY_TO_CATEGORY',
  MAKER_REQUEST = 'MAKER_REQUEST',
  MAKER_REQUEST_APPROVE = 'MAKER_REQUEST_APPROVE',
  MAKER_REQUEST_REJECT = 'MAKER_REQUEST_REJECT',
  WATCHLIST_SEARCH_ONE = 'WATCHLIST_SEARCH_ONE',
  WATCHLIST_SEARCH_MANY = 'WATCHLIST_SEARCH_MANY',
  CASE_DATA_EXPORT = 'CASE_DATA_EXPORT',
  SEND_TRANSACTION = 'SEND_TRANSACTION',
  TEST = 'TEST',
  CASE_CLOSE = 'CASE_CLOSE',
  CASE_CLOSE_BULK = 'CASE_CLOSE_BULK',
  CASE_BULK_ACTION = 'CASE_BULK_ACTION',
  CASE_ASSIGNEE = 'CASE_ASSIGNEE',
  CASE_UNASSIGMENT = 'CASE_UNASSIGMENT',
  CASE_GO = 'CASE_GO',
  CASE_HOLD = 'CASE_HOLD',
  CASE_COMMENT = 'CASE_COMMENT',
  CASE_COMMENT_EDIT = 'CASE_COMMENT_EDIT',
  CASE_COMMENT_DOMAIN = 'CASE_COMMENT_DOMAIN',
  CASE_COMMENT_EDIT_DOMAIN = 'CASE_COMMENT_EDIT_DOMAIN',
  CASE_ATTACHMENT = 'CASE_ATTACHMENT',
  CASE_ATTACHMENT_DOMAIN = 'CASE_ATTACHMENT_DOMAIN',
  CASE_TAGGED_FIELD = 'CASE_TAGGED_FIELD',
  ENTITY_UPDATE = 'ENTITY_UPDATE',
  ENTITY_COMMENT = 'ENTITY_COMMENT',
  ENTITY_COMMENT_EDIT = 'ENTITY_COMMENT',
  COMPONENT_WORK = 'COMPONENT_WORK',
  COMPONENT_USER_THROW = 'COMPONENT_USER_THROW',
  CASE_RUN_ACTION = 'CASE_RUN_ACTION',
  KYC_RUN_ACTION = 'KYC_RUN_ACTION',
  CATEGORY_UPDATE_NAME = 'CATEGORY_UPDATE_NAME',
  REMOVE_ITEM_FROM_CATEGORY = 'REMOVE_ITEM_FROM_CATEGORY',
  ALERT_COMMENT = 'ALERT_COMMENT',
  TRANSACTION_DATA_EXPORT = 'TRANSACTION_DATA_EXPORT',
  CASE_BATCH_CLOSE = 'CASE_BATCH_CLOSE',
  AUDIT_LOG_EXPORT = 'AUDIT_LOG_EXPORT',
  ALERT_EXPORT = 'ALERT_EXPORT',
  CASE_LOG_EXPORT = 'CASE_LOG_EXPORT',
  LIST_DATA_EXPORT = 'LIST_DATA_EXPORT',
  CASE_VARIABLE_PANEL_EXPORT = 'CASE_VARIABLE_PANEL_EXPORT',
  CHART_EXPORT = 'CHART_EXPORT',
  GRAPH_EXPORT = 'GRAPH_EXPORT',
  ADMIN_AUDITS_EXPORT = 'ADMIN_AUDITS_EXPORT',
  LOGIN_AUDITS_EXPORT = 'LOGIN_AUDITS_EXPORT',
  CRA_RESULT_EXPORT = 'CRA_RESULT_EXPORT',
  MATCH_MANY_LOG_EXPORT = 'MATCH_MANY_LOG_EXPORT',
  MATCH_MANY_LOG_RESULT_EXPORT = 'MATCH_MANY_LOG_RESULT_EXPORT',
  ACH_DATA_EXPORT = 'ACH_DATA_EXPORT',
}

export interface Role extends BaseDocument {
  name: string;
  description?: string;
  menu: Array<MenuPermission>;
  enabled: boolean;
  createdDate: number;
  lastUpdated: number;
}

export interface MenuPermission {
  baseMenu: string;
  subMenu: Array<MenuPermission>;
  permission: PermissionType;
}

export interface Tenant {
  id: string;
  subscription: string;
  createdDate: number;
  elasticUsername: string;
  elasticPassword: string;
  mongoUsername: string;
  mongoPassword: string;
  serviceUsername: string;
  servicePassword: string;
}

export interface User {
  id?: string;
  username: string;
  firstName: string;
  lastName: string;
  groups: Array<string>;
  email: string;
  role: CommonRoles;
  roles: Array<string>;
  lastLoginDate?: number;
  userType: UserType;
  preferences: UserPreferences;
  activeRiskType: ActiveRiskType;
  riskType: 'fraud' | 'compliance' | 'all';
  enabled: boolean;
  //not for db, for runtime
  mergedRoles?: MenuPermission[];
  groupRoles?: string[];
  filters?: DefaultFilter[];
  createdAt?: number;
  updatedAt?: number;
}

export interface Group {
  id?: string;
  readonly name: string;
  description: string;
  role: CommonRoles;
  roles: string[];
  permission: MemberPermission;
  users: string[];
  enabled?: boolean;
  createdAt?: number;
  updatedAt?: number;
}

export interface UserState {
  readonly username: string;
  readonly role: CommonRoles;
  readonly groups: Array<string>;
  readonly permission: MemberPermission;
}

export interface UserPreferences {
  timeZone: string | undefined;
  twoFA: boolean;
  language: string;
  fontSize: string;
}

export interface DefaultFilter {
  component: ComponentName;
  id?: string;
  riskType?: ActiveRiskType;
  filter: { filter: CustomFilter };
}

export interface UserSystemAttributes {
  id: string;
  username: string;
  filters: DefaultFilter[];
  table: UserSystemTableAttributes[];
  login?: UserSystemLogin;
  tabOrders?: TabOrderItem[];
}

export interface TabOrderItem {
  key: string;
  label: string;
  disabled?: boolean;
  testid?: string;
}

export interface UserSystemLogin {
  loginAttempts: UserLoginAttempt[];
  read: boolean;
}

export interface UserLoginAttempt {
  success: boolean;
  timestamp: number;
  ip: number;
}

export interface UserSystemTableAttributes {
  component: ComponentName;
  id?: string;
  columnOrder: string[];
  hiddenColumns: string[];
  pageSize: number;
}
